import { instance } from "api/apiBase";
import {
  Property,
  Unit,
  UnitTemplateOptionListResponse
} from "types/landlord/address";
import { ApplicationNote, NoteData } from "shared/app/ActivityLog/types";
import { Application, PreviewApplicationRentByUnit } from "types/application";
import { Money, Verification } from "shared/types";

export const QUERY_GET_PROPERTIES = "properties";
export const QUERY_SYNC_PROPERTY_STATUS = "sync_properties_status";
export const QUERY_SYNC_UNITS_STATUS = "sync_units_status";
export const QUERY_SYNC_RENTERS_STATUS = "sync_renters_status";

export const MUTATION_SYNC_PROPERTIES = "sync_properties";
export const MUTATION_SYNC_UNITS = "sync_units";
export const MUTATION_SYNC_RENTERS = "sync_renters";

export const MUTATION_SYNC_PROPERTY_BY_ID = "sync_property_by_id";
export const MUTATION_SYNC_UNIT_BY_ID = "sync_unit_by_id";
export const QUERY_SYNC_PROPERTY_BY_ID_STATUS = "sync_property_by_id_status";
export const QUERY_SYNC_UNIT_BY_ID_STATUS = "sync_unit_by_id_status";

export const QUERY_GET_APPFOLIO_PROPERTIES = "appfolio_properties";
export const MUTATION_ADD_PROPERTY_BY_ID = "add_property_by_id";
export const MUTATION_EXCLUDE_PROPERTY = "exclude_appfolio_property";
export const MUTATION_INCLUDE_PROPERTY = "include_appfolio_property";

const api = {
  getProperties: async ({
    page = 1,
    searchTerm = "",
    per_page = 12
  }: { page?: number; searchTerm?: string; per_page?: number } = {}) => {
    return (
      await instance.get(`/portal/v1/properties`, {
        params: {
          per_page,
          page,
          search: searchTerm
        }
      })
    ).data;
  },

  getProperty: async (propertyId: string) => {
    return (await instance.get(`/portal/v1/properties/${propertyId}`)).data;
  },
  getUnitsOfProperty: async ({
    propertyId,
    searchTerm = "",
    per_page = 12,
    page = 1
  }: {
    propertyId: number;
    page?: number;
    searchTerm?: string;
    per_page?: number;
  }) => {
    return (
      await instance.get(`/portal/v1/properties/${propertyId}/units`, {
        params: { per_page, page, search: searchTerm }
      })
    ).data;
  },
  getUnits: async ({
    page = 1,
    searchTerm = "",
    per_page = 12
  }: { page?: number; searchTerm?: string; per_page?: number } = {}) => {
    return (
      await instance.get(`/portal/v1/units`, {
        params: {
          per_page,
          page,
          search: searchTerm
        }
      })
    ).data;
  },
  getAllUnits: async () => {
    return (await instance.get(`/portal/v1/units`)).data;
  },
  getUnitById: async (unitId: string) => {
    return (await instance.get(`/portal/v1/units/${unitId}`)).data;
  },
  getUnitByPropertyAndUnitId: async (propertyId: string, unitId: string) => {
    return (
      await instance.get<Unit>(
        `/portal/v1/properties/${propertyId}/units/${unitId}`
      )
    ).data;
  },
  getUnitTemplateOptions: async (unitId: string) => {
    return (
      await instance.get<UnitTemplateOptionListResponse>(
        `/portal/v1/units/${unitId}/template_options`
      )
    ).data;
  },
  editUnit: async (
    unitId: number,
    settings: {
      title?: string;
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
      available_at?: string;
      occupied?: boolean;
      plan?: {
        sqft?: number;
        beds?: number;
        baths?: number;
      };
      external_id?: string;
      description?: string;
      media_session_key?: string;
      media?: {
        content_type: string;
        resolutions: {
          asset_id: string;
          resolution: string;
        }[];
      }[];
      pms_monthly_rent_amount_override?: boolean;
      pms_home_rent_amount_override?: boolean;
      pms_site_rent_amount_override?: boolean;
      pms_occupied_override?: boolean;
      pms_available_at_override?: boolean;
      pms_monthly_rent_amount?: number;
      lockbox_id?: number | null;
    }
  ): Promise<Unit> => {
    return await instance.patch(`/portal/v1/units/${unitId}`, { ...settings });
  },
  deleteUnit: async (unitId: number) => {
    return (await instance.delete(`/portal/v1/units/${unitId}`)).data;
  },
  updateMagicLink: async (
    linkId: number,
    { active, listed }: { active: boolean; listed?: boolean }
  ) => {
    return (
      await instance.patch(`/portal/v1/magic_links/${linkId}`, {
        active,
        listed
      })
    ).data;
  },
  getAllAplications: async ({
    page = 1,
    searchTerm = "",
    per_page = 10
  }: { page?: number; searchTerm?: string; per_page?: number } = {}) => {
    return (
      await instance.get(`/portal/v1/applications`, {
        params: {
          per_page,
          page,
          search: searchTerm
        }
      })
    ).data;
  },
  getApplicationById: async (id: string) => {
    return (await instance.get<Application>(`/portal/v1/applications/${id}`))
      .data;
  },
  approveApplication: async (
    applicationId: string,
    details: {
      conditions?: string[];
      note_message?: string;
      email_message?: string;
      skip_sending_email?: boolean;
      conditional_approval?: boolean;
      send_applicants_to_pms?: boolean;
    }
  ): Promise<Application> => {
    return (
      await instance.post(`/portal/v1/applications/${applicationId}/approve`, {
        ...details
      })
    ).data;
  },
  rejectApplication: async (
    applicationId: string,
    details: {
      reasons: string[];
      note_message?: string;
      email_message?: string;
      skip_sending_email?: boolean;
    }
  ): Promise<Application> => {
    return (
      await instance.post(`/portal/v1/applications/${applicationId}/reject`, {
        ...details
      })
    ).data;
  },

  addApplicationNote: async (
    applicationId: number,
    data: NoteData
  ): Promise<ApplicationNote> => {
    return (
      await instance.post(
        `/portal/v1/boom_screen_applications/${applicationId}/notes`,
        data
      )
    ).data;
  },
  editApplicationNote: async (
    applicationId: number,
    noteId: number,
    data: NoteData
  ): Promise<ApplicationNote> => {
    return (
      await instance.patch(
        `/portal/v1/boom_screen_applications/${applicationId}/notes/${noteId}`,
        data
      )
    ).data;
  },
  deleteApplicationNote: async (applicationId: number, noteId: number) => {
    return (
      await instance.delete(
        `/portal/v1/boom_screen_applications/${applicationId}/notes/${noteId}`
      )
    ).data;
  },
  updateAllLinks: async (property_id: string, active: boolean) => {
    return (
      await instance.patch(`/portal/v1/magic_links/batch_update_by_property`, {
        property_id,
        active
      })
    ).data;
  },
  inviteApplicantByUnitId: async (email: string, unit_id: string) => {
    return (
      await instance.post("/portal/v1/magic_links", {
        email,
        unit_id
      })
    ).data;
  },
  inviteApplicantByPropertyId: async (email: string, property_id: string) => {
    return (
      await instance.post("/portal/v1/magic_links", {
        email,
        property_id
      })
    ).data;
  },
  batchInviteApplicants: async (invites: {
    unit_id?: number;
    application_id?: number;
    invitees: {
      email: string;
      role: string;
    }[];
  }) => {
    return (
      await instance.post("/portal/v1/magic_links/batch", {
        ...invites
      })
    ).data;
  },
  addUnits: async (
    propertyId: string,
    unit: {
      title: string;
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
      available_at?: string;
      occupied?: boolean;
    }
  ) => {
    return (
      await instance.post(`/portal/v1/properties/${propertyId}/units`, {
        ...unit
      })
    ).data;
  },
  batchAddUnits: async (units: Unit[], propertyId: string) => {
    return await instance.post(
      `/portal/v1/properties/${propertyId}/units/batch`,
      { units }
    );
  },
  addProperty: async ({
    use_property_group_verification_template,
    name,
    owner_name,
    address,
    units,
    external_id,
    verification_template_id
  }: {
    use_property_group_verification_template: boolean;
    name: string;
    owner_name?: string;
    address: {
      address1: string;
      address2?: string;
      postal_code: string;
      city: string;
      country: string;
      state_or_province: string;
    };
    units: Unit[];
    external_id?: string;
    verification_template_id?: string;
  }): Promise<Property> => {
    return await instance
      .post("/portal/v1/properties", {
        use_property_group_verification_template,
        name,
        external_id,
        address,
        units,
        verification_template_id,
        owner_name
      })
      .then((p) => p.data);
  },
  getPropertyRenters: async (propertyId: string) => {
    return (await instance.get(`/portal/v1/properties/${propertyId}/renters`))
      .data;
  },
  editProperty: async (data: {
    propertyId: number;
    name?: string;
    owner_name?: string;
    address?: {
      address1: string;
      address2?: string;
      postal_code?: string;
      city?: string;
      country?: string;
      state_or_province?: string;
    };
    description?: string;
    external_id?: string;
    verification_template_id?: string;
    prescreening_template_identifier?: string;
    use_property_group_verification_template?: boolean;
    amenity_ids?: number[];
    pet_policy?: {
      cats_allowed: boolean;
      dogs_allowed: boolean;
    };
    media?: {
      content_type: string;
      resolutions: {
        asset_id: string;
        resolution: string;
      }[];
    }[];
    media_session_key?: string;
    branding?: {
      logo?: string;
      primary_color?: string;
      landlord_name?: string;
      landlord_phone?: string;
      palette?: Record<string, string>;
    };
    template_options?: {
      id?: number;
      name: string;
      verification_template_identifier: string;
    }[];
  }) => {
    return (
      await instance.patch(`/portal/v1/properties/${data.propertyId}`, data)
    ).data;
  },
  deleteProperty: async (propertyId: string) => {
    return (await instance.delete(`/portal/v1/properties/${propertyId}`)).data;
  },
  requestPropertyManagementSystemSync: async (resource: string) => {
    return (
      await instance.post(
        `/portal/v1/property_management_system/${resource}/sync`
      )
    ).data;
  },
  getPropertyManagementSystemSyncStatus: async (resource: string) => {
    return (
      await instance.get(
        `/portal/v1/property_management_system/${resource}/sync_status`
      )
    ).data;
  },
  requestPropertyManagementSystemSyncById: async (
    resource: string,
    id: string
  ) => {
    return (
      await instance.post(
        `/portal/v1/property_management_system/${resource}/${id}/sync`
      )
    ).data;
  },
  getPropertyManagementSystemSyncByIdStatus: async (
    resource: string,
    id: string
  ) => {
    return (
      await instance.get(
        `/portal/v1/property_management_system/${resource}/${id}/sync_status`
      )
    ).data;
  },
  updateApplicationUnit: async (
    application_id: string,
    unit_id: number,
    rent_data: {
      change_type: "use_current" | "use_listed" | "manual";
      monthly_rent_amount?: Money;
      home_rent_amount?: Money;
      site_rent_amount?: Money;
    }
  ): Promise<Application> => {
    return (
      await instance.patch<Application>(
        `/portal/v1/applications/${application_id}/unit`,
        {
          unit_id,
          rent_data
        }
      )
    ).data;
  },
  getManualAnimalScreeningReport: async (
    verification_id: number
  ): Promise<Verification> => {
    return (
      await instance.post(`/portal/v1/pet_screenings/sync`, {
        verification_id
      })
    ).data;
  },
  deleteMediaItem: async (mediaId: string) => {
    return (
      await instance.delete(`/portal/v1/listings/media/${mediaId}/delete`)
    ).data;
  },

  // Appfolio-specific functions for browsing and adding properties
  getAppfolioProperties: async ({
    page = 1,
    limit = 100,
    sortBy,
    sortOrder,
    search,
    statusFilter
  }: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: string;
    search?: string;
    statusFilter?: string;
  }) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(sortBy && { sortBy }),
      ...(sortOrder && { sortOrder }),
      ...(search && { search }),
      ...(statusFilter && { status_filter: statusFilter })
    });

    return (
      await instance.get(`/portal/v1/properties/appfolio/browse?${params}`)
    ).data;
  },

  addPropertyById: async (propertyId: string) => {
    return (
      await instance.post(`/portal/v1/properties/appfolio/add`, {
        property_id: propertyId
      })
    ).data;
  },
  getPreviewApplicationRentByUnit: async (
    application_id: string,
    unit_id: number
  ): Promise<PreviewApplicationRentByUnit> => {
    return (
      await instance.get(
        `/portal/v1/applications/${application_id}/preview_application_rent_by_unit`,
        {
          params: { unit_id: unit_id.toString() }
        }
      )
    ).data;
  },

  excludeProperty: async (propertyId: string) => {
    return (
      await instance.post(`/portal/v1/properties/appfolio/exclude`, {
        property_id: propertyId
      })
    ).data;
  },

  includeProperty: async (propertyId: string) => {
    return (
      await instance.post(`/portal/v1/properties/appfolio/include`, {
        property_id: propertyId
      })
    ).data;
  }
};

export default api;
